import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '@components/AppIcon';
import Button from '@components/ui/Button';
import Input from '@components/ui/Input';
import Select from '@components/ui/Select';

const ApplicationModal = ({ isOpen, onClose, selectedPosition = '' }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
    watch
  } = useForm();

  const watchedFiles = watch('resume');

  // Set the selected position when modal opens
  useEffect(() => {
    if (selectedPosition && isOpen) {
      setValue('position', selectedPosition);
    }
  }, [selectedPosition, isOpen, setValue]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const positionOptions = [
    { value: '', label: 'Select a position' },
    { value: 'Senior Ophthalmologist', label: 'Senior Ophthalmologist' },
    { value: 'Optometrist', label: 'Optometrist' },
    { value: 'Medical Receptionist', label: 'Medical Receptionist' },
    { value: 'Ophthalmic Technician', label: 'Ophthalmic Technician' },
    { value: 'General Application', label: 'General Application' }
  ];

  const experienceOptions = [
    { value: '', label: 'Select experience level' },
    { value: 'Entry Level', label: 'Entry Level (0-1 years)' },
    { value: 'Junior', label: 'Junior (1-3 years)' },
    { value: 'Mid-Level', label: 'Mid-Level (3-5 years)' },
    { value: 'Senior', label: 'Senior (5-10 years)' },
    { value: 'Expert', label: 'Expert (10+ years)' }
  ];

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      console.log('Application submitted:', data);
      
      setSubmitStatus('success');
      reset();
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      // Validate file type and size
      const allowedTypes = ['.pdf', '.doc', '.docx'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      const fileExtension = '.' + file?.name?.split('.')?.pop()?.toLowerCase();
      
      if (!allowedTypes?.includes(fileExtension)) {
        alert('Please upload a PDF, DOC, or DOCX file');
        e.target.value = '';
        return;
      }
      
      if (file?.size > maxSize) {
        alert('File size must be less than 5MB');
        e.target.value = '';
        return;
      }
    }
  };

  const handleModalClose = () => {
    setSubmitStatus(null);
    reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
        onClick={handleModalClose}
      />
      
      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-primary/10 to-accent/10 px-6 py-4 border-b border-gray-200 rounded-t-2xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Icon name="Send" size={20} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-foreground">
                    Submit Your Application
                  </h2>
                  <p className="text-sm text-text-secondary">
                    Join the Eye&U Kenya team
                  </p>
                </div>
              </div>
              <button
                onClick={handleModalClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Icon name="X" size={20} className="text-gray-500" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-success/10 border border-success/20 rounded-lg flex items-start space-x-3">
                <Icon name="CheckCircle" size={20} className="text-success mt-0.5" />
                <div>
                  <h4 className="font-semibold text-success">Application Submitted Successfully!</h4>
                  <p className="text-success/80 text-sm">We'll review your application and get back to you within 3 business days.</p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg flex items-start space-x-3">
                <Icon name="AlertCircle" size={20} className="text-error mt-0.5" />
                <div>
                  <h4 className="font-semibold text-error">Submission Failed</h4>
                  <p className="text-error/80 text-sm">There was an error submitting your application. Please try again.</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="grid md:grid-cols-2 gap-4">
                {/* Personal Information */}
                <div>
                  <Input
                    label="First Name"
                    placeholder="Enter your first name"
                    error={errors?.firstName?.message}
                    {...register('firstName', {
                      required: 'First name is required',
                      minLength: {
                        value: 2,
                        message: 'First name must be at least 2 characters'
                      }
                    })}
                  />
                </div>

                <div>
                  <Input
                    label="Last Name"
                    placeholder="Enter your last name"
                    error={errors?.lastName?.message}
                    {...register('lastName', {
                      required: 'Last name is required',
                      minLength: {
                        value: 2,
                        message: 'Last name must be at least 2 characters'
                      }
                    })}
                  />
                </div>

                <div>
                  <Input
                    label="Email Address"
                    type="email"
                    placeholder="your.email@example.com"
                    error={errors?.email?.message}
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email address'
                      }
                    })}
                  />
                </div>

                <div>
                  <Input
                    label="Phone Number"
                    type="tel"
                    placeholder="+254 700 000 000"
                    error={errors?.phone?.message}
                    {...register('phone', {
                      required: 'Phone number is required',
                      pattern: {
                        value: /^[\+]?[\d\s\-\(\)]{10,}$/,
                        message: 'Please enter a valid phone number'
                      }
                    })}
                  />
                </div>

                {/* Position Information */}
                <div>
                  <Select
                    label="Position of Interest"
                    options={positionOptions}
                    error={errors?.position?.message}
                    {...register('position', {
                      required: 'Please select a position'
                    })}
                  />
                </div>

                <div>
                  <Select
                    label="Experience Level"
                    options={experienceOptions}
                    error={errors?.experience?.message}
                    {...register('experience', {
                      required: 'Please select your experience level'
                    })}
                  />
                </div>
              </div>

              {/* Cover Letter */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Cover Letter / Message
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-ring focus:border-transparent medical-transition resize-none"
                  rows={4}
                  placeholder="Tell us why you're interested in working with Eye&U Kenya and what makes you a great fit for this role..."
                  {...register('coverLetter', {
                    required: 'Please provide a cover letter or message',
                    minLength: {
                      value: 50,
                      message: 'Cover letter must be at least 50 characters'
                    }
                  })}
                />
                {errors?.coverLetter && (
                  <p className="mt-1 text-sm text-error">{errors?.coverLetter?.message}</p>
                )}
              </div>

              {/* Resume Upload */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Resume / CV
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-ring focus:border-transparent medical-transition file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary/10 file:text-primary hover:file:bg-primary/20"
                    {...register('resume', {
                      required: 'Please upload your resume'
                    })}
                  />
                </div>
                {errors?.resume && (
                  <p className="mt-1 text-sm text-error">{errors?.resume?.message}</p>
                )}
                <p className="mt-1 text-xs text-text-secondary">
                  Accepted formats: PDF, DOC, DOCX (Max size: 5MB)
                </p>
              </div>

              {/* Additional Information */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-text-primary mb-2">
                  Additional Information (Optional)
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-border rounded-md focus:ring-2 focus:ring-ring focus:border-transparent medical-transition resize-none"
                  rows={3}
                  placeholder="Any additional information you'd like to share (certifications, languages, special skills, etc.)"
                  {...register('additionalInfo')}
                />
              </div>

              {/* Terms and Conditions */}
              <div className="mt-4">
                <label className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    className="mt-1 rounded border-border text-primary focus:ring-primary"
                    {...register('terms', {
                      required: 'You must agree to the terms and conditions'
                    })}
                  />
                  <span className="text-sm text-text-secondary">
                    I agree to the processing of my personal data for recruitment purposes and confirm that the information provided is accurate.
                  </span>
                </label>
                {errors?.terms && (
                  <p className="mt-1 text-sm text-error">{errors?.terms?.message}</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="mt-6 flex space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleModalClose}
                  className="flex-1"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="default"
                  disabled={isSubmitting}
                  className="flex-1 flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <>
                      <Icon name="Loader2" size={16} className="animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="Send" size={16} />
                      <span>Submit Application</span>
                    </>
                  )}
                </Button>
              </div>

              <div className="mt-3 text-center">
                <p className="text-xs text-text-secondary">
                  By submitting this form, you agree to our privacy policy. We'll never share your information with third parties.
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationModal;